import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import Button from "@/components/Common/Button";
import AddressForm from "@/components/Common/forms/AddressForm";
import AutosizeTextarea from "@/components/Common/forms/AutosizeTextarea";
import InputField from "@/components/Common/forms/InputField";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import {PhoneInput} from "@/components/ui/PhoneInput";
import useCompanyProfileSave from "@/hooks/useCompanyProfileSave";
import {useSettingsDirty} from "@/hooks/useSettingsNavigation";
import {useIsAdmin} from "@/hooks/useUserHooks";
import BrandLogo from "@/pages/profile/BrandLogo";
import InterestSelection from "@/pages/profile/InterestSelection";
import {Can} from "@/permissions";
import {useCategoryStore} from "@/store/category";
import {useSettingsCompanyStore} from "@/store/company";
import type {TSettingsScope} from "@/store/types";
import {useEffect, type FC} from "react";
import {useTranslation} from "react-i18next";

const InfoPane: FC<{scope: TSettingsScope}> = ({scope}) => {
  const {t} = useTranslation(["common", "settings", "agency_profile"]);
  const isAdmin = useIsAdmin();
  const maxAllowedCategories = useCategoryStore(state => state.maxAllowedCategories);
  const company = useSettingsCompanyStore(state => state.company);
  const errors = useSettingsCompanyStore(state => state.errors);
  const setValue = useSettingsCompanyStore(state => state.setValue);
  const validateAddress = useSettingsCompanyStore(state => state.addressValidation);
  const validatePersonalInfo = useSettingsCompanyStore(state => state.personalInfoValidation);
  const validatePhoneNumber = useSettingsCompanyStore(state => state.phoneNumberValidation);
  const validateCompany = useSettingsCompanyStore(state => state.companyFormValidation);

  const {isSaving, save} = useCompanyProfileSave({companyId: scope.id});
  useSettingsDirty(useSettingsCompanyStore(state => state.hasUnsavedChanges()));

  useEffect(() => {
    if (company.id === scope.id) validateCompany();
  }, [company.id, scope.id]);

  if (company.id !== scope.id) {
    return (
      <div className="flex flex-col gap-8">
        <SkeletonLoader className="h-4.5 max-w-40" />
        <SkeletonLoader className="size-44 self-center rounded-full" />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <SkeletonLoader className="h-5 max-w-32" />
            <SkeletonLoader className="h-11 rounded-lg" />
          </div>
          <div className="flex flex-col gap-2">
            <SkeletonLoader className="h-5 max-w-32" />
            <SkeletonLoader className="h-11 rounded-lg" />
          </div>
          {Array.from({length: 2}).map((_, index) => (
            <div className="flex flex-col gap-2 sm:flex-row" key={index}>
              {Array.from({length: 2}).map((__, fieldIndex) => (
                <div className="flex flex-1 flex-col gap-2" key={fieldIndex}>
                  <SkeletonLoader className="h-5 max-w-28" />
                  <SkeletonLoader className="h-11 rounded-lg" />
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <SkeletonLoader className="h-4.5 max-w-24" />
            <SkeletonLoader className="h-5 max-w-72" />
          </div>
          {Array.from({length: 4}).map((_, index) => (
            <div className="flex flex-col gap-2" key={index}>
              <SkeletonLoader className="h-5 max-w-40" />
              <SkeletonLoader className="h-21 rounded-lg" />
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-2">
              <SkeletonLoader className="h-4.5 max-w-28" />
              <SkeletonLoader className="h-5 max-w-24" />
            </div>
            <SkeletonLoader className="h-5 max-w-80" />
          </div>
          <div className="flex flex-wrap gap-x-1 gap-y-2">
            {Array.from({length: 8}).map((_, index) => (
              <SkeletonLoader className="h-10 w-24 rounded-md" key={index} />
            ))}
          </div>
        </div>
        <SkeletonLoader className="h-10 rounded-lg sm:max-w-32" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <span className="text-neutral-black text-lg leading-none font-bold">{t("settings:company_profile")}</span>
      <div className="flex flex-col items-center gap-3">
        <BrandLogo company={company} isCompanyProfile />
      </div>
      <Can I="update" a="CompanyDetails" passThrough>
        {allowed => (
          <>
            <div className="flex flex-col gap-4">
              <InputField
                className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
                error={errors.company_name}
                isDisabled={!allowed}
                label={t("common:companiesName")}
                labelClassName="text-neutral-black text-sm font-medium"
                onBlur={() => validatePersonalInfo()}
                onChange={value => setValue("company_name", value)}
                placeholder={t("common:companiesNamePlaceholder")}
                value={company.company_name ?? ""}
              />
              <AddressForm
                cityKey="address_city"
                className="gap-1"
                errors={errors}
                inputClassName="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm bg-white"
                countryKey="country"
                isDisabled={!allowed}
                key={company.id}
                labelClassName="text-neutral-black text-sm font-medium"
                placeDescriptionKey="place_description"
                placeIdKey="place_id"
                postalClassName="sm:w-[calc(50%-4px)]"
                postalKey="address_postal"
                storeForm={company}
                storeSetter={setValue}
                streetKey="address_street"
                titleClassName="text-neutral-black text-sm font-medium"
                validateForm={() => validateAddress()}
              />
              <div className="flex flex-col gap-2 sm:flex-row">
                <PhoneInput
                  buttonClassName="border-neutral-grey-light-dark bg-white h-11 w-22"
                  containerClassName="flex-1 gap-1 flex flex-col"
                  defaultCountry="CH"
                  disabled={!allowed}
                  error={errors.phonenumber}
                  labelClassName="text-neutral-black text-sm font-medium"
                  numberInputClassName="border-neutral-grey-light-dark bg-white h-11 px-3 text-sm md:text-sm"
                  onBlur={() => validatePhoneNumber()}
                  onChange={value => setValue("phonenumber", value)}
                  value={company.phonenumber ?? ""}
                />
                <InputField
                  className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
                  containerClassName="flex-1"
                  error={errors.website}
                  isDisabled={!allowed}
                  label={t("common:companiesWebsite")}
                  labelClassName="text-neutral-black text-sm font-medium"
                  onBlur={() => validatePersonalInfo()}
                  onChange={value => setValue("website", value)}
                  placeholder={t("common:companiesWebsitePlaceholder")}
                  value={company.website ?? ""}
                />
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <InputField
                  className="border-neutral-grey-light-dark h-11 rounded-lg pr-3 pl-8.5 text-sm"
                  containerClassName="flex-1"
                  icon={<InstagramIcon className="text-primary-dark-solid absolute inset-y-0 left-3 my-auto size-4" />}
                  isDisabled={!allowed}
                  label={t("common:companiesInstagram")}
                  labelClassName="text-neutral-black text-sm font-medium"
                  onChange={value => setValue("username", value)}
                  placeholder={t("common:companiesInstagramUsernamePlaceholder")}
                  value={company.username ?? ""}
                />
                <InputField
                  className="border-neutral-grey-light-dark h-11 rounded-lg pr-3 pl-8.5 text-sm"
                  containerClassName="flex-1"
                  icon={<TiktokIcon className="text-primary-dark-solid absolute inset-y-0 left-3 my-auto size-4" />}
                  isDisabled={!allowed}
                  label={t("common:companiesTikTok")}
                  labelClassName="text-neutral-black text-sm font-medium"
                  onChange={value => setValue("tiktok_username", value)}
                  placeholder={t("common:companiesTikTokUsernamePlaceholder")}
                  value={company.tiktok_username ?? ""}
                />
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-neutral-black text-lg leading-none font-bold">{t("settings:about")}</span>
                <span className="text-neutral-grey-dark text-sm">{t("settings:about_description")}</span>
              </div>
              <AutosizeTextarea
                className="border-neutral-grey-light-dark rounded-lg p-3 text-sm md:placeholder:text-sm"
                disabled={!allowed}
                htmlFor="bio"
                id="bio"
                maxRows={6}
                minRows={3}
                onChange={({target: {value}}) => setValue("bio", value)}
                placeholder={t("common:companiesBioPlaceholder")}
                title={t("common:companiesBio")}
                titleClassName="text-neutral-black text-sm font-medium"
                value={company.bio ?? ""}
              />
              <AutosizeTextarea
                className="border-neutral-grey-light-dark rounded-lg p-3 text-sm md:placeholder:text-sm"
                disabled={!allowed}
                htmlFor="companyOverview"
                id="companyOverview"
                maxRows={6}
                minRows={3}
                onChange={({target: {value}}) => setValue("company_overview", value)}
                placeholder={t("common:company_overview_placeholder")}
                title={t("common:company_overview")}
                titleClassName="text-neutral-black text-sm font-medium"
                value={company.company_overview ?? ""}
              />
              <AutosizeTextarea
                className="border-neutral-grey-light-dark rounded-lg p-3 text-sm md:placeholder:text-sm"
                disabled={!allowed}
                htmlFor="productsOverview"
                id="productsOverview"
                maxRows={6}
                minRows={3}
                onChange={({target: {value}}) => setValue("products_overview", value)}
                placeholder={t("common:your_products_overview_placeholder")}
                title={t("common:your_products_overview")}
                titleClassName="text-neutral-black text-sm font-medium"
                value={company.products_overview ?? ""}
              />
              <AutosizeTextarea
                className="border-neutral-grey-light-dark rounded-lg p-3 text-sm md:placeholder:text-sm"
                disabled={!allowed}
                htmlFor="customerPainPoints"
                id="customerPainPoints"
                maxRows={6}
                minRows={3}
                onChange={({target: {value}}) => setValue("customer_pain_points", value)}
                placeholder={t("common:your_companys_pain_points_placeholder")}
                title={t("common:your_companys_pain_points")}
                titleClassName="text-neutral-black text-sm font-medium"
                value={company.customer_pain_points ?? ""}
              />
            </div>
          </>
        )}
      </Can>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-neutral-black text-lg leading-none font-bold">{t("settings:categories")}</span>
            <span className="text-neutral-grey-dark text-sm">
              {t("settings:categories_selected", {max: maxAllowedCategories, selected: company.interests?.length ?? 0})}
            </span>
          </div>
          <span className="text-neutral-grey-dark text-sm">
            {t("settings:categories_description", {max: maxAllowedCategories})}
          </span>
        </div>
        <InterestSelection />
      </div>
      <Can I="update" a="CompanyDetails">
        <Button
          buttonStyle="Gradient"
          className="sticky bottom-0 h-10 w-full px-5 sm:static sm:w-fit"
          isDisabled={isSaving || (Object.keys(errors).length > 0 && !isAdmin)}
          isLoading={isSaving}
          onClick={save}>
          {t("common:saveChanges")}
        </Button>
      </Can>
    </div>
  );
};

export default InfoPane;
